import type { InfraConfigUI, TargetHealthUI, BarState } from "../../types";

/** Capitalize first letter */
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Provider display labels */
const PROVIDER_LABELS: Record<string, string> = {
  rusha_hosted: "Rusha Hosted",
  aws: "AWS",
  gcp: "GCP",
  azure: "Azure",
  self_hosted: "Self-hosted",
};

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? capitalize(provider);
}

/**
 * Derive the InfraStatusBar state from infra config + target health.
 *
 * Rules:
 * - No config → warning, "Set up"
 * - Rusha hosted → success, "Manage"
 * - Cloud provider, some unhealthy targets → warning, "Manage"
 * - Cloud provider, all healthy → success, "Manage"
 */
export function deriveBarState(
  infra: InfraConfigUI | null,
  targets: TargetHealthUI[],
): BarState {
  if (!infra || !infra.cloudProvider) {
    return {
      variant: "warning",
      message: "No infrastructure configured",
      action: "set_up",
    };
  }

  const provider = providerLabel(infra.cloudProvider);

  if (infra.cloudProvider === "rusha_hosted") {
    return {
      variant: "success",
      message: `Rusha Hosted${infra.region ? ` · ${infra.region}` : ""}`,
      action: "manage",
    };
  }

  const totalTargets = targets.length;
  const healthyTargets = targets.filter((t) => t.health === "healthy").length;

  if (totalTargets === 0) {
    return {
      variant: "warning",
      message: `${provider} connected · no targets registered`,
      action: "manage",
    };
  }

  if (healthyTargets < totalTargets) {
    return {
      variant: "warning",
      message: `${provider} · ${healthyTargets} of ${totalTargets} targets reachable`,
      action: "manage",
    };
  }

  return {
    variant: "success",
    message: `${provider} connected · ${totalTargets} target(s)`,
    action: "manage",
  };
}
